// Live sync: while active, every plan edit is mirrored to the connected device
// as it happens. Rather than build a per-edit command for each control, this
// keeps a snapshot of what the device last received (captured from a full
// readback when sync turns on) and, on each debounced flush, re-translates the
// whole plan and sends only the addresses whose value changed. The whole-plan
// translate is pure (no IO), so the diff is cheap; the IO is just the deltas.
// Connection lifecycle (connect/disconnect) is owned by the caller, which holds
// the connection open for the duration of the session.

import type { DeviceModel } from "../../models/types";
import type { Plan } from "../plan";
import { vdGet, vdSet, vdSetStr } from "../platform";
import { silentKey, PARAMS } from "./params";
import type { ParamName, ParamSpec, SilentFamily } from "./params";
import {
  addrKey,
  cmdAddr,
  collisionKey,
  collisionOwners,
  formatAddrKey,
  planToCommands,
  planToFollowOnlyAddrs,
  nameControl,
  planToNameWrites,
  unverifiedGuessAt,
} from "./translate";
import type { SharedOwners, NameWrite, VdCommand, WriteScope } from "./translate";
import { ClipSafeHold, confirmedAddrs, reachedAndFailed, sendConverging } from "./client";
import { SETTLE_TIMEOUT_MS, writeSettle } from "./settle";
import type { PendingWrites } from "./settle";
import { carrierOf, onExcludedBy, otherSwitchId } from "../input-lock";
import type { PlanWriteWatch } from "../plan-history";

// Coalesce rapid edits (a slider drag fires per pixel) into one flush so the
// single-threaded device worker is not flooded; the snapshot diff means only the
// final value of each address is sent.
//
// This is a trailing THROTTLE, not a debounce that re-arms: a window absorbs
// whatever lands in it and then flushes. A re-arming debounce never fired at all
// while a drag was in motion — measured, a 500 ms drag at 25 ms intervals sent
// nothing until the pointer stopped, so the device only heard the end of every
// move. One flush costs a whole-plan translate + diff, measured at 0.20 ms for
// the URX44V default plan (782 commands) in both V8 and WebKit, so flushing per
// window rather than per gesture is 0.2% of a core.
export const DEBOUNCE_MS = 120;
/** The longest an edit stream holds back a flush that would converge (see `schedule`). Past it
 *  the window is left to fire: the converge then runs once in the middle of the stream, and the
 *  stream's own edits go out behind it at the ordinary cadence. */
export const CONVERGE_DEFER_MAX_MS = 500;

// Params whose write makes the device move other values (the catalog flags these with
// sideEffect), split by who owns what moved. CONVERGE = the device reset values the plan
// authors, so they are pushed back; REFETCH = the device wrote values the plan only
// mirrors (the EQ 1-knob's four bands), so the owner node is read instead — pushing
// would fight it. See ParamSpec.sideEffect.
const CONVERGE = new Set<string>();
const REFETCH = new Set<string>();
// What each refetch head hands to the device, for the flush that carries both repairs
// (ParamSpec.drives). Empty for a head whose driven addresses the plan stops emitting.
const DRIVES = new Map<string, readonly string[]>();
// Which silent family each converge head resets (ParamSpec.resets), read the way DRIVES is
// — one catalogue entry per head rather than a second list here to keep in step.
const RESETS = new Map<string, SilentFamily>();
for (const [name, spec] of Object.entries(PARAMS as Record<string, ParamSpec>)) {
  if (spec.sideEffect === "converge") {
    CONVERGE.add(name);
    if (spec.resets) RESETS.set(name, spec.resets);
  } else if (spec.sideEffect === "refetch") {
    REFETCH.add(name);
    if (spec.drives?.length) DRIVES.set(name, spec.drives);
  }
}

// The address key comes from translate.ts, which uses the same one to decide which
// commands collapse: a second spelling here would let a collapsed command miss its
// snapshot entry.
const nameKey = (w: NameWrite): string => `${w.param}:${w.y}`;

/** What a writable address resolves to, for device-follow routing of a notify. */
export interface FollowAddr {
  /** Catalog parameter name (its follow strategy comes from PARAMS[name]). */
  name: ParamName;
  /** Owner node id the address belongs to (undefined for a global address). */
  node?: string;
  /** True when the param is flagged follow: "direct" — apply the notify value
   *  straight into the plan with no read-back; false → re-read the owner node. */
  direct: boolean;
}

export interface LiveSyncHooks {
  getModel: () => DeviceModel;
  getPlan: () => Plan;
  /** A write failed; sync is already stopped — the caller drops the connection. `guess` names
   *  the unverified mapping the failed address rests on, where one does. */
  onError: (message: string, guess?: string) => void;
  /** A flush sent `count` writes — for an optional, quiet "→ device" status. */
  onSent: (count: number) => void;
  /** A converge in this flush read these addresses back and found the device at the plan's
   *  value, together with THE PLAN THOSE ADDRESSES CAME FROM. Both, because a caller acting on
   *  it maps addresses to plan keys and one address is a different key under a different effect
   *  type: the converge runs against a clone taken before its own await, so resolving that join
   *  against the live plan answers with whatever type is selected by the time the answer is
   *  used. Absent when no converge ran — a direct write is acknowledged and not re-read, and an
   *  acknowledgement says the unit accepted a write, not that it kept it. */
  onConfirmed?: (confirmed: ReadonlySet<number>, sent: Plan) => void;
  /** Two or more plan owners resolved to one device address and the emitted set
   *  kept the last; the rest carried a different value and were dropped. Reported
   *  once per distinct owner set, not once per flush. */
  onCollapsed: (owners: SharedOwners[]) => void;
  /** Write scope for the session (see translate.ts WriteScope). Read at every
   *  snapshot / flush, so the one planToCommands filter also scopes the notify
   *  registration and echo detection. Absent = "all". */
  getScope?: () => WriteScope;
  /** Read these nodes back from the device and apply them to the plan — the repair a
   *  "refetch" sideEffect needs. Supplied by the caller rather than called here, so
   *  this module keeps its one direction of travel (plan → device) and the device→plan
   *  inverse stays in the one place that owns it. Absent = no refetch (the browser
   *  build, and the tests that do not exercise it). Resolves the private copy its read
   *  ran against (readback.readIntoPlan) — that copy is what the device holds as far as
   *  the read established it, and so what the snapshot re-base measures from. Null when
   *  the plan it read into has been replaced: there is then nothing to re-base. The read
   *  carries each node's body and not the node's own name, so the re-base keeps what the
   *  snapshot held for those names.
   *
   *  `pending` is what THIS flush put on the device and the device acked. The unit acks
   *  a write before the value is readable, and this read is issued in the same
   *  millisecond as the last ack, so the read holds until the unit has spoken for the
   *  addresses it is about to ask for, and answers each of them with WHAT THE UNIT
   *  ANNOUNCED (settle.ts, readback's writeOverlay). Never with what was sent: an acked
   *  write the unit silently discarded is indistinguishable from one it took, and
   *  answering it from the send would record our value as device truth with no diff left
   *  to retry. Both halves happen inside the read, where its own clone and witness cover
   *  an edit made meanwhile — hence a value handed over rather than a wait taken here.
   *  Without it the read put the value the edit had just replaced back into the plan, the
   *  capture below recorded it as device truth, and the unit's own notify for our write
   *  then failed isEcho and was reconciled as a device-side change. */
  refetchNodes?: (nodes: ReadonlySet<string>, pending: PendingWrites, edits?: PlanWriteWatch) => Promise<Plan | null>;
  /** Start watching the plan's edits (plan-history.PlanWriteWitness). A flush whose writes
   *  will provoke a refetch opens one before its first write, and hands it to that read: an
   *  edit made after that instant is carried by none of the writes, and the read, opened only
   *  once the writes have returned, keeps it as one made while it runs. Absent = the read
   *  watches from its own start, and a refetch head is never held back for the next flush. */
  watchEdits?: () => PlanWriteWatch;
  /**
   * Read the addresses the unit announces nothing for into the plan, in front of a write
   * that would go out over them (`readback.applySilentState`).
   *
   * Three families announce nothing when the unit's own panel moves them — the FX effect
   * arrays, the insert-FX engine arrays and D.Gain — so the plan's copy of them can be
   * arbitrarily old, and a write derived from that copy discards what the operator tuned
   * there. There are THREE boundaries where such a write goes out and this is called at each
   * under its own condition, so one flush calls it 0 to 3 times: in front of the head
   * writes where some head this flush is MOVING declares `ParamSpec.resets`, in front of a
   * source's D.Gain the snapshot holds no entry for (a source a channel is routed back to,
   * whose D.Gain the write had stopped carrying), and in front of the converge where the
   * flush converges at all. An ordinary edit takes none, and a converging head that resets
   * no silent family takes only the last.
   *
   * In front of its own HEAD writes, `only` names the families those heads reset —
   * `silentKey` entries, one per family per node, from `ParamSpec.resets`. The head refills
   * them with the incoming type's factory values, so what the unit is holding is the
   * outgoing effect and this is the last moment it can be read. `keepHeads` leaves the
   * plan's own heads standing, the operator having already chosen the incoming type, and
   * files the values under the keys the head the UNIT is on owns. The flush derives its
   * commands again afterwards, so what goes out carries what this merged.
   *
   * In front of the CONVERGE, `exclude` names those same reset entries and everything else
   * is taken. A converge re-reads the whole write scope and re-sends whatever differs, so
   * it is the one thing that puts the plan's copy on the unit at addresses no edit named;
   * reading the reset entries there would adopt the reset and throw away the values the
   * converge is about to put back. Per FAMILY and not per node, so the other families of a
   * head's own node are still taken: a channel carries a COMP/EQ type and an insert effect
   * at once, and only one of them is ever the head's.
   *
   * Called with the plan the write about to go out is derived from, so what it reads is in
   * that copy. A read that FAILS ends the session, and the flush's own generation check is
   * what stops the write behind it — which write that is depends on the call: the head
   * writes are still ahead of the first, and only the converge is ahead of the second.
   * Absent = no park (the browser build, and the tests that do not exercise it).
   */
  parkSilent?: (scope: {
    exclude?: ReadonlySet<string>;
    only?: ReadonlySet<string>;
    keepHeads?: boolean;
  }) => Promise<void>;
  /** The follow address set may have moved — re-register against it. Called at the END of a
   *  flush whose capture rebuilt the set, never inside one: a re-registration unsubscribes
   *  before it subscribes, so running it mid-flush would drop the very notifies the refetch's
   *  settle is waiting for. Idempotent on the callee's side (DeviceFollow.refresh compares
   *  the set), so this says "it may have changed" rather than "it did". Absent = nothing
   *  follows this session (the browser build). */
  reregister?: () => void;
}

/** One address's writes that are acked but not yet announced, oldest first. `at` is a
 *  wall clock (Date.now), which is what lets an entry expire on a link that has gone
 *  silent — see the fields that use it for why that is not settle.ts's axis. */
type PendingQueue<V> = Array<{ value: V; at: number }>;

/** Drop the leading entries older than `cutoff`, in one splice rather than a shift per
 *  entry — a repeated shift is a memmove each, so draining a long queue that way would
 *  cost O(n^2) element moves inside the notify callback that triggered it. */
function dropExpired<V>(q: PendingQueue<V>, cutoff: number): void {
  let i = 0;
  while (i < q.length && q[i].at < cutoff) i++;
  if (i) q.splice(0, i);
}

export class LiveSync {
  private active = false;
  // Which session a flush belongs to. Bumped by begin() and end(), and read after every
  // await in the send loops: `end()` is synchronous while a loop is not, so a flush that
  // checked only at its entry finishes sending against a link the app has already
  // disconnected — and after a model switch (loadPlan -> deactivateLive -> end) it sends
  // addresses the plan on screen does not have. A generation rather than the `active`
  // flag, because a session that ended and began again inside one await leaves the flag
  // at the value the flush entered on while every snapshot under it has been rebuilt.
  private sessionGen = 0;
  // Aborted by end(), and handed to a flush's converge: a converge is a loop of reads, sends
  // and settles, and the generation is asked only once it returns, so this is what stops it
  // between its own round trips. Replaced by begin(), so a session's signal is its own.
  private sessionAbort = new AbortController();
  // The flush in flight, settled when it finishes — what `idle()` answers.
  private flushDone: Promise<void> = Promise.resolve();
  private readonly snapshot = new Map<number, number>();
  private readonly nameSnapshot = new Map<string, string>();
  // Name address -> owner node. Kept apart from `index` because a name is not a
  // VdCommand: it has no catalog entry, no follow strategy and no value in the
  // numeric snapshot. Registering the addresses without this map would make every
  // rename an UNKNOWN address, which follow.ts escalates to a whole-device read.
  private readonly nameIndex = new Map<number, string>();
  // Every address to register for device-side notifies, as numeric [paramId, x, y]
  // triples, built alongside the snapshot so the registration needs no key re-parse.
  // Wider than the writable set: names and the read-only follows are in here too.
  private followAddrList: Array<[number, number, number]> = [];
  // A capture rebuilt the follow address set, so the follow layer's registration may be
  // describing the previous one. Consumed at the end of the flush rather than where it is
  // set: capture() runs INSIDE a flush (after the converge, after the refetch), and a
  // re-registration unsubscribes before it subscribes — doing that mid-flush would drop the
  // notifies the refetch's own settle is waiting for.
  private followSetStale = false;
  // Address → {name, owner node, direct?} for the writable parameter set, built
  // with the snapshot from the same planToCommands pass. Lets device-follow route
  // an incoming notify to a direct apply or a scoped readback with no key re-parse.
  private readonly index = new Map<number, FollowAddr>();
  // Every direct-follow notify the session has taken, as address → the last value the
  // device reported and the journal position it arrived at. A re-base rebuilds the whole
  // snapshot from the private clone a read ran against, and that clone was taken when the
  // read was ISSUED — so it cannot carry a notify that landed while the read was in
  // flight, and the rebuild would drop the entry noteDirect wrote for it. The device's
  // next notify for that address would then match the stale snapshot entry, be dropped as
  // our own echo, and the following flush would write the operator's own move on the
  // hardware back off the board. Keyed by address, so it holds one entry per writable
  // address however long the session runs.
  private readonly directJournal = new Map<number, { value: number; at: number }>();
  private directSeq = 0;
  // The last value the unit announced per address, echo or not, at the journal position it
  // arrived at (`directSeq`, shared with the direct journal so one mark orders both). The
  // diff never reads it: a non-direct change waits in the plan for the follow read it
  // scheduled, and sending against it would write the plan's older value over the unit's
  // own change before that read lands. The +48V / HI-Z exclusion reads it (`unitHolds`),
  // because an ON that goes out reaches the unit as the unit IS — HI-Z turned on at its
  // panel is on from its announcement, not from the read. A later announcement replaces an
  // entry, the flush's own write removes it, and a capture removes the ones its read
  // supersedes: issued after the announcement, over the node the address belongs to, and
  // reading the address.
  private readonly announced = new Map<number, { value: number; at: number }>();
  // The converge in flight: what it leaves out (`exclude`), and the part of that which is a
  // switch the unit turned on at its own panel and the other switch on the same channel
  // (`ownOns`). An announcement arriving while it runs can add to both (`isEcho`).
  private converge: { ownOns: Set<number>; exclude: Set<number> } | null = null;
  // The A.Gain writes a Clip Safe OFF waits behind, for the session: an OFF in one flush waits
  // for an A.Gain another flush sent (`ClipSafeHold`). Replaced by end().
  private clipSafeHold = new ClipSafeHold();
  // A switch ON the exclusion held back is still waiting in the plan, and the A.Gain it
  // lowered with it (`carrierOf`). Whatever can move the view it was held against — an
  // announcement, a capture — schedules a flush to ask again, since no edit will. A read that
  // finds the other switch on takes the ON back, and the plan then agrees with the snapshot; an
  // ON pressed while that read ran and not refused goes out; and one pressed before it was
  // issued takes the read's value, as every key nobody edited while it ran does.
  private onHeld = false;
  // Writes the device has ACKED but not yet announced. The snapshot cannot represent
  // them: it holds ONE value per address, so a second write to the same address moves
  // it past the first, and when the first write's announcement finally arrives it no
  // longer equals anything the snapshot holds — and is applied as a device-side change,
  // writing a value the operator has already replaced back onto the board. The unit
  // announces a numeric write ack+58-151 ms after the ack (reference/work/vd) against a
  // 120 ms flush window, so a drag reaches that overlap on real hardware. One queue per
  // address, oldest first, retained for the same window a settle waits (settle.ts).
  //
  // NOT writeSettle's job, though it is the obvious neighbour and the next reader will
  // ask. `PendingWrites` holds addresses without the values sent — settle.ts says that
  // is deliberate, since the answer it gives is the value the DEVICE announced — it is
  // built per flush and closed in the same flush's finally, where this has to survive
  // into the next one, and it is one entry per address where the whole case is two
  // outstanding writes to one. It also only ever reaches settle on the sideEffect and
  // refetch paths, so a plain fader drag never builds one at all.
  //
  // Wall clock rather than settle's notify sequence, and the two are not the same
  // question: settle asks "did the announcement arrive after my write went out", which
  // is ordering, while this asks "is this notify recent enough to still be one of mine",
  // which needs elapsed time. A sequence bound would never expire on a quiet link —
  // no notifies, no advance — and that is exactly when an unannounced write should be
  // forgotten. SETTLE_TIMEOUT_MS is borrowed for the LENGTH, not the axis.
  private readonly pendingValues = new Map<number, PendingQueue<number>>();
  // Addresses whose numeric write is on the wire and not yet acked, with the value it
  // carries. A flush sends one command at a time and never overlaps another flush, so an
  // address is in here at most once. Together with `pendingValues` it answers
  // `hasUnannouncedWrite`: from the moment a write is issued until its announcement is
  // taken as an echo.
  private readonly inFlight = new Map<number, number>();
  // In-flight writes whose announcement arrived before their ack and was taken as their
  // echo (`isEcho`), so the ack queues no announcement that has already come.
  private readonly echoedInFlight = new Set<number>();
  // The same for the string writes, keyed as `pendingNames` is.
  private readonly inFlightNames = new Set<string>();
  /**
   * Every address this session wrote recently, with the settle mark taken before its
   * own `vdSet` — the same record the flush builds for its own refetch, kept at session
   * scope so the DEVICE-FOLLOW reads can have it too.
   *
   * They need it for the reason the refetch does. A follow reconcile reads whatever
   * node the operator's hardware gesture touched, and that node's addresses can include
   * ones this session's flush wrote 40 ms ago — inside the measured 9-204 ms window in
   * which the unit still answers a GET with the PRE-write value. The read then takes
   * that stale value as device truth, and the unit's own notify for our write, which
   * would have repaired it, is consumed as an echo instead. What the operator sees is
   * their toggle flipping back for ~0.6 s until the idle full reconcile re-reads past
   * the window — and in between, plan and snapshot agree on a value the device does not
   * hold, which is harness invariant 3 clause B exactly.
   *
   * Retained by wall clock, like `pendingValues` and for the same reason: a sequence
   * bound never expires on a quiet link, which is precisely when a stale entry should
   * be forgotten.
   */
  private readonly recentWrites = new Map<number, { mark: number; node?: string; at: number }>();
  // The name twin. Separate for the reason isEchoName is separate — names live in their
  // own snapshot and key on `${param}:${y}` with no x. Its measured overtake margin is
  // wider (ack+1-102 ms), so this half is a structural twin rather than a reachable
  // defect today; keeping one rule for one structure is what stops the two drifting.
  private readonly pendingNames = new Map<string, PendingQueue<string>>();
  // Bumped by every write the FOLLOW side makes into a snapshot — noteDirect's single entry
  // and capture()'s two whole rebuilds. A flush translates the plan once and then awaits
  // per command, so the lists it walks can grow older than the snapshots it diffs each
  // command against: the loop reaches an address the device moved mid-flush, finds its
  // frozen command disagreeing with the fresh entry, and sends a value the plan has
  // stopped holding — the device's own previous one, back over the hand still on the knob.
  // This is what lets a running flush notice and re-take its translate.
  //
  // A COUNTER rather than a dirty flag, because the flush has two independent consumers
  // (the value loop and the name loop) and a flag one of them cleared would be invisible to
  // the other. Each holds its own last-seen value instead. Separate from `directSeq`, which
  // orders the direct journal and the announcements and whose marks both compare with `>`.
  //
  // It is a SNAPSHOT write standing in for a PLAN write, which works only because every
  // device-side plan write is synchronously followed by one (follow.ts applyDirect →
  // noteDirect; every reconcile → resync). A follow path that merged into the plan without
  // touching a snapshot would re-open the hole here in silence.
  private snapshotEpoch = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;
  private pending = false;
  // The last flush had to converge (a sideEffect param went out), which re-reads
  // the whole write scope and settles between rounds — seconds, not milliseconds.
  // That alone does not decide anything: it only says the next schedule() should
  // ask the diff whether the same is about to happen again (convergePending).
  private lastFlushConverged = false;
  // When the current window was first re-armed for a pending converge (`schedule`); null while
  // no window is being held.
  private deferredSince: number | null = null;
  // Identity of the owner set the last collapse report named (collisionKey), so a
  // standing collision is said once rather than once per flush — it is a structural
  // condition ("these two nodes share one engine"), not a per-edit event, and a drag
  // anywhere in the plan re-derives it every window. It re-arms on a flush that finds
  // no collision at all AND on every capture(), because a capture re-bases from device
  // truth and the collision it named may no longer be the plan's. Deliberately NOT
  // re-armed by elapsed time: the emitted list carries a standing collision whatever
  // the operator is editing, so a timed re-arm would repeat it during unrelated work.
  private collapsedKey = "";

  constructor(private readonly hooks: LiveSyncHooks) {}

  isActive(): boolean {
    return this.active;
  }

  /**
   * Whether a flush is armed, running, or queued behind the running one: the plan may hold
   * an edit the unit has not been sent, or a write is on the wire. What device follow holds
   * a reconcile off (`DeviceFollowHooks` `deferReconcile`).
   *
   * A read taken then answers such an address with the value the edit replaces — the flush
   * has not sent it, or the unit answers the pre-write value until it announces the write —
   * and the merge takes a read's value wherever the plan still holds what it held when the
   * read was issued, which an edit made before that does. Once this answers false every
   * edit has been sent and acked, and the read's settle waits out their announcements
   * (`recentPending`). A converge round is inside a flush, so a read never lands in one.
   */
  isWriting(): boolean {
    return this.timer !== null || this.flushing;
  }

  /** Settles once the flush running now, if any, has finished — what a teardown waits on
   *  before it disconnects, so the flush's last command goes over the link it started on. A
   *  flush the session ended under stops at its next generation check, and a converge inside
   *  it at its next round trip. */
  idle(): Promise<void> {
    return this.flushDone;
  }

  private scope(): WriteScope {
    return this.hooks.getScope?.() ?? "all";
  }

  /** Start syncing. Call right after a full readback, passing the private copy that
   *  read ran against — without it an edit made during the multi-second starting read
   *  is enshrined as a value the device was already given. Omit it only where the plan
   *  itself is device truth (no read spanned an await). */
  begin(deviceView?: Plan): void {
    this.clipSafeHold = new ClipSafeHold();
    this.directJournal.clear();
    this.directSeq = 0;
    this.announced.clear();
    this.onHeld = false;
    // Session boundary: nothing acked on a previous link can be an echo on this one.
    // Deliberately NOT cleared in capture() — a write acked just before a resync is
    // exactly the one whose announcement is still in flight, and clearing there would
    // reopen the hole this queue exists to close. The retention bounds that judgement.
    this.pendingValues.clear();
    this.pendingNames.clear();
    this.inFlight.clear();
    this.echoedInFlight.clear();
    this.inFlightNames.clear();
    // Same session boundary: a mark taken on a previous link means nothing on this one.
    this.recentWrites.clear();
    this.capture(deviceView);
    // The caller subscribes for this session itself (main.ts: live.begin then follow.begin),
    // so the capture above is already accounted for and must not make the next flush ask again.
    this.followSetStale = false;
    this.sessionAbort = new AbortController();
    this.active = true;
    this.sessionGen++;
  }

  /** Re-capture the device-truth snapshot after a device-follow readback has pulled the
   *  plan into agreement with the device, so the next outgoing diff measures from the
   *  device truth (and so device-side notifies for the just-read values register as
   *  echoes). Pass the copy the readback ran against, or an edit made during that read
   *  is recorded as device truth and stops being a diff — and `since`, the mark taken
   *  when that read was issued, or the direct notifies it could not carry are dropped.
   *  `nodes` are the nodes that read covered (absent = the whole device): only their
   *  addresses are re-based, and the unit's announcements on them from before `since` are
   *  what the read has now carried. */
  resync(deviceView?: Plan, since?: number, nodes?: ReadonlySet<string>): void {
    this.capture(deviceView, since, nodes);
  }

  /** The journal position to hand back to resync() with the view a read produces. Taken
   *  when the read is ISSUED: every direct notify after it is device truth the read's
   *  private clone predates, and is restored over the rebuild — and every announcement
   *  after it stays the unit's last word (`announced`). */
  directMark(): number {
    return this.directSeq;
  }

  /**
   * What this session wrote recently, shaped for a device read's settle (see
   * `recentWrites`). `nodes` scopes it the way the flush's own refetch handle is
   * scoped: an address belonging to a node the read covers goes in `mustSettle`, so the
   * read waits it out rather than answering from inside its staleness window; pass
   * nothing for a whole-device read, where every address is in scope.
   *
   * `mustAnnounce` is deliberately empty: the flush arms the watch for its own writes,
   * so a second one here would report one silent write twice and arm two reconciles for
   * it. The flush arms it in two places — beside the refetch epilogue for the addresses
   * that epilogue's read does not cover, and after the write loop for a flush that has
   * no epilogue at all.
   *
   * Expired entries are dropped as they are met, so the map cannot grow across a long
   * session on its own.
   */
  recentPending(nodes?: ReadonlySet<string>): PendingWrites {
    const now = Date.now();
    const written = new Map<number, number>();
    const mustSettle = new Set<number>();
    for (const [k, w] of this.recentWrites) {
      if (now - w.at > SETTLE_TIMEOUT_MS) {
        this.recentWrites.delete(k);
        continue;
      }
      written.set(k, w.mark);
      if (!nodes || (w.node !== undefined && nodes.has(w.node))) mustSettle.add(k);
    }
    return { written, mustSettle, mustAnnounce: new Set() };
  }

  /** Patch one snapshot entry to a device-reported value (a direct follow notify),
   *  so the next outgoing diff measures from it without a full re-translate. The
   *  notify value is the same broker raw value the snapshot stores, and a direct
   *  change never alters the writable-address set or the name snapshot, so patching
   *  the single entry keeps the snapshot in agreement with the device. */
  noteDirect(paramId: number, x: number, y: number, value: number): void {
    const k = addrKey(paramId, x, y);
    this.snapshot.set(k, value);
    this.directJournal.set(k, { value, at: ++this.directSeq });
    this.snapshotEpoch++;
  }

  /** Every parameter address to register for device-side change notifies, as
   *  [paramId, x, y] triples. NOT the writable set, which is what it used to be and
   *  what the name used to say: it is the writable set plus the name addresses plus
   *  the read-only follows (`planToFollowOnlyAddrs`) — an address the app only reads
   *  still has to be registered, or the unit announces a front-panel change to nobody.
   *  Captured with the snapshot, so it must be called after begin()/resync(). Read-only. */
  followAddrs(): Array<[number, number, number]> {
    return this.followAddrList;
  }

  /** What the snapshot currently holds, as "paramId:x:y" → value. A copy, and the
   *  packed keys are rendered back to the published string form: the caller is the
   *  trace probe, and a poisoned snapshot is only detectable by comparing what it
   *  records against what was actually sent (see src/ui/trace-probe.ts). */
  snapshotEntries(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [k, v] of this.snapshot) out[formatAddrKey(k)] = v;
    return out;
  }

  /**
   * Whether the snapshot already holds this value for this address — the unit is still
   * where we last put it.
   *
   * `isEcho`'s question without `isEcho`'s side effect, and the two are not
   * interchangeable: that one CONSUMES the pending entry it matches, because a notify is
   * an announcement that belongs to exactly one write. A caller that is not answering a
   * notify — the silent-address park, which is answering a READ — must not spend one, or
   * the announcement of that write reaches the follow layer as a device-side change.
   *
   * An address the snapshot does not track answers false: we have sent it nothing, so
   * whatever the unit holds there is news.
   */
  holdsSent(paramId: number, x: number, y: number, value: number): boolean {
    return this.snapshot.get(addrKey(paramId, x, y)) === value;
  }

  /** Whether an incoming device notify equals the snapshotted device truth — i.e.
   *  it is the echo of a value we just wrote (or already knew), not a fresh
   *  device-side change. An address we do not track returns false (treat as a
   *  change worth reconciling). */
  isEcho(paramId: number, x: number, y: number, value: number): boolean {
    const k = addrKey(paramId, x, y);
    // Every numeric notify passes here once, echo or not, so this is where the unit's last
    // word per address is kept — an echo included, since the unit announces what it ended
    // up holding.
    this.announced.set(k, { value, at: ++this.directSeq });
    if (this.onHeld) this.schedule();
    const converge = this.converge;
    const name = converge ? this.index.get(k)?.name : undefined;
    if (converge && name)
      for (const own of this.ownOnPair({ name, paramId, x, y })) {
        converge.ownOns.add(own);
        converge.exclude.add(own);
      }
    // The write still on the wire first: a notify carrying its value is its announcement,
    // overtaking its ack, and the unit holds what we sent. Every acked write queued before
    // it goes with it — left queued, one of them would swallow the unit moving back to its
    // value for the rest of the retention window — so a later notify carrying an older
    // value is the unit's own move.
    if (this.inFlight.get(k) === value) {
      this.pendingValues.delete(k);
      this.echoedInFlight.add(k);
      return true;
    }
    // Then the acked writes, and the match CONSUMES its entry and every one queued before
    // it, for the same reason (`takePending`).
    if (this.takePending(this.pendingValues, k, value)) return true;
    return this.snapshot.get(k) === value;
  }

  /** What the unit holds at an address as far as this session knows: its last announcement
   *  where one came after the snapshot last learned the address, else the snapshot — which
   *  holds an ON the flush sent, whatever a read in flight sampled before it. Asked about the
   *  +48V / HI-Z switches alone (see `announced`). */
  private unitHolds(k: number): number | undefined {
    return this.announced.get(k)?.value ?? this.snapshot.get(k);
  }

  /** `unitHolds` for a device read deciding which +48V / HI-Z ONs it refuses
   *  (input-lock.ts `SwitchSession.holdsOn`). */
  unitHoldsOn(paramId: number, x: number, y: number): boolean {
    return Boolean(this.unitHolds(addrKey(paramId, x, y)));
  }

  /** Whether the switch command `c` stands for is an ON `values` (the plan's) holds, the unit
   *  has not taken, and the exclusion holds back. */
  private onWaits(c: Pick<VdCommand, "name" | "paramId" | "x" | "y">, values: ReadonlyMap<number, number>): boolean {
    const k = addrKey(c.paramId, c.x, c.y);
    const value = values.get(k);
    if (value === undefined || this.snapshot.get(k) === value) return false;
    const other = onExcludedBy(c, value);
    return other !== null && Boolean(this.unitHolds(other));
  }

  /** For a +48V / HI-Z command whose switch the unit turned on at its own panel — announced
   *  ON, and not what the snapshot holds — that address and the other switch's on the same
   *  channel; nothing for any other command. */
  private ownOnPair(c: Pick<VdCommand, "name" | "paramId" | "x" | "y">): number[] {
    const k = addrKey(c.paramId, c.x, c.y);
    const a = this.announced.get(k);
    if (a === undefined || this.snapshot.get(k) === a.value) return [];
    const other = onExcludedBy(c, a.value);
    return other === null ? [] : [k, other];
  }

  /**
   * Whether this session has a numeric write to this address that the unit has not yet
   * announced: issued and not acked, or acked with its announcement still queued inside
   * the retention window. A device-side value that arrives in that span is one the unit
   * held before our write took effect, since the unit announces changes in the order it
   * makes them; the write replaces it once it lands.
   */
  hasUnannouncedWrite(paramId: number, x: number, y: number): boolean {
    return this.unannounced(this.inFlight, this.pendingValues, addrKey(paramId, x, y));
  }

  /** The string half of `hasUnannouncedWrite`: a name or a catalogued string write to
   *  this address that the unit has not yet announced. */
  hasUnannouncedName(paramId: number, y: number): boolean {
    return this.unannounced(this.inFlightNames, this.pendingNames, `${paramId}:${y}`);
  }

  private unannounced<K, V>(inFlight: { has(key: K): boolean }, queues: Map<K, PendingQueue<V>>, key: K): boolean {
    if (inFlight.has(key)) return true;
    const q = queues.get(key);
    if (!q) return false;
    dropExpired(q, Date.now() - SETTLE_TIMEOUT_MS);
    if (!q.length) queues.delete(key);
    return q.length > 0;
  }

  /** Append a write we have just been acked for, so its late announcement is still
   *  recognisable after the snapshot has moved past it. */
  private notePending<K, V>(queues: Map<K, PendingQueue<V>>, key: K, value: V): void {
    const now = Date.now();
    const q = queues.get(key);
    if (!q) {
      queues.set(key, [{ value, at: now }]);
      return;
    }
    // Pruned HERE, not only when a notify asks. `takePending` sweeps the one key it is
    // asked about, so an address the unit never announces would keep every write it
    // ever took — and several params emit no notify at all when written (the converge /
    // refetch families), which makes that the ordinary case rather than the lost-notify
    // one. At the 120 ms flush cadence this holds each queue to the writes issued
    // inside the retention window, so it is ~3 entries rather than one per flush for
    // the life of the session.
    dropExpired(q, now - SETTLE_TIMEOUT_MS);
    q.push({ value, at: now });
  }

  /**
   * Consume the queued write `value` announces, dropping everything older than the
   * settle window first. True = this notify is the late echo of one of our own writes.
   *
   * MUTATES, so it must be asked once per notify — which is what the single call site
   * (follow.ts's gate, through main.ts) does. The match is the NEWEST entry carrying the
   * value, and everything up to and including it is spliced out, not just the match: a
   * stale earlier entry left in front would otherwise match a genuinely later device value
   * and swallow it, and a queue holding the value twice — a drag that returned to it —
   * would keep the write between the two.
   *
   * `Date.now()` and not `performance.now()`: vitest fakes Date by default and does not
   * fake performance, so a performance clock makes the retention untestable — and the
   * failure mode is a test that passes because the entry never expires.
   */
  private takePending<K, V>(queues: Map<K, PendingQueue<V>>, key: K, value: V): boolean {
    const q = queues.get(key);
    if (!q) return false;
    dropExpired(q, Date.now() - SETTLE_TIMEOUT_MS);
    let i = q.length - 1;
    while (i >= 0 && q[i].value !== value) i--;
    if (i >= 0) q.splice(0, i + 1);
    if (!q.length) queues.delete(key);
    return i >= 0;
  }

  /** Stop syncing and cancel any pending flush. Does not touch the connection. */
  end(): void {
    this.active = false;
    this.clipSafeHold = new ClipSafeHold();
    this.sessionGen++;
    this.sessionAbort.abort();
    this.announced.clear();
    this.onHeld = false;
    this.pendingValues.clear();
    this.pendingNames.clear();
    this.inFlight.clear();
    this.echoedInFlight.clear();
    this.inFlightNames.clear();
    this.recentWrites.clear();
    this.pending = false;
    this.lastFlushConverged = false;
    this.collapsedKey = "";
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Note a plan change; a flush runs at the end of the current window. No-op when
   *  inactive. */
  schedule(): void {
    if (!this.active) return;
    if (this.timer !== null) {
      // A window is already armed and will pick this change up with the rest —
      // unless what is waiting in it would converge, in which case re-arm so a
      // continuous stream waits for quiet rather than starting a converge round
      // per window. Asking the diff, not "did the last flush converge": that
      // latch alone would silence the first drag after any converge, whatever it
      // touched, which is the behaviour the throttle exists to remove.
      //
      // Held for CONVERGE_DEFER_MAX_MS at most: past it the armed window fires. The flush it
      // starts sends the converge head, so the diff stops carrying one and the rest of the
      // stream is throttled as usual — the converge runs once, not once per window.
      if (!this.lastFlushConverged || !this.convergePending()) return;
      if (this.deferredSince !== null && Date.now() - this.deferredSince >= CONVERGE_DEFER_MAX_MS) return;
      this.deferredSince ??= Date.now();
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      this.deferredSince = null;
      void this.flush();
    }, DEBOUNCE_MS);
  }

  /** Would flushing now send a sideEffect param? Runs the same whole-plan translate
   *  the flush does (0.2 ms for a URX44V plan), so it is only consulted while the
   *  previous flush converged — the state where the answer changes what happens. */
  private convergePending(): boolean {
    const model = this.hooks.getModel();
    const plan = this.hooks.getPlan();
    for (const c of planToCommands(model, plan, this.scope())) {
      if (CONVERGE.has(c.name) && this.snapshot.get(cmdAddr(c)) !== c.vdValue) return true;
    }
    return false;
  }

  /**
   * Rebuild the device-truth snapshot. SHAPE comes from the live plan (which addresses
   * exist at all, and so what is registered for notifies); VALUES come from
   * `deviceView`, the private copy a readback ran against. Absent = the live plan is
   * itself device truth, which is only so when nothing was read across an await.
   *
   * The split is the whole point: an address the operator moved during the read holds
   * their value in the live plan and the device's in the view, so it stays a diff and
   * the next flush sends it. Snapshotting the live plan instead records their edit as a
   * value the device was given, and nothing ever retries it.
   *
   * The NAME snapshot takes both from `deviceView`, and needs no split: the flush
   * iterates the live plan's name writes and sends any whose key the snapshot does not
   * hold, so a name typed during the read is sent for exactly the reason an address the
   * view does not carry is — absence from the snapshot IS the diff. The extra entries a
   * name dropped during the read leaves behind are never consulted.
   *
   * `since` is the journal mark the read carried; the direct-follow notifies that landed
   * after it are re-applied over the rebuild. Those entries are DEVICE truth the view
   * predates, not the plan's — the rule that a re-base never takes a value from the live
   * plan is unchanged.
   */
  /** A plan's writable addresses and the values it would send, as the flush's diff and the
   *  snapshot's rebuild both need them. One spelling, because the two are required to
   *  agree: the flush diffs against a snapshot this same function built, and an address
   *  key or a value derived differently on one side would be a silent mismatch. */
  private commandValues(model: DeviceModel, plan: Plan, scope: WriteScope): Map<number, number> {
    return new Map(planToCommands(model, plan, scope).map((c) => [cmdAddr(c), c.vdValue] as const));
  }

  private capture(
    deviceView?: Plan,
    since?: number,
    nodes?: ReadonlySet<string>,
    unread?: ReadonlySet<number>,
    /** Whether the read behind this view carried the covered nodes' own names. */
    readNames = true,
  ): void {
    // A re-base re-authors the plan from the device, so a collision reported against the
    // pre-read plan may already be gone — a reconcile reads the shared address once and
    // assigns it to both owners, which erases the divergence. Nothing schedules a flush
    // after a reconcile (planValuesChanged, unlike markChanged, does not), so the report
    // latch has to clear here or the next genuine loss of that same pair is swallowed.
    this.collapsedKey = "";
    this.snapshotEpoch++;
    const model = this.hooks.getModel();
    const scope = this.scope();
    const plan = this.hooks.getPlan();
    // Every caller passes a private copy (readback's clone, or the converge's own),
    // so a view is never the live plan itself.
    const device = deviceView ? this.commandValues(model, deviceView, scope) : null;
    // `unread`: addresses the read behind this view left out. The snapshot goes on holding
    // what it held there, and their announcements stand.
    const kept = unread ? [...unread].map((k) => [k, this.snapshot.get(k)] as const) : [];
    // A read that covered some nodes (`nodes`) says nothing about the others: there the
    // snapshot goes on holding what it held — nothing, where it held nothing — names included,
    // so a value the plan holds and the unit was never sent stays a difference. A read that
    // carried no node names (`readNames`) says nothing about those either, on any node.
    const prior = nodes ? new Map(this.snapshot) : null;
    const priorNames = nodes || !readNames ? new Map(this.nameSnapshot) : null;
    const covered = (node: string | undefined): boolean => !nodes || (node !== undefined && nodes.has(node));
    this.snapshot.clear();
    this.nameSnapshot.clear();
    const commands = planToCommands(model, plan, scope);
    for (const c of commands) {
      const k = cmdAddr(c);
      // An address the view does not carry grew after the read was issued (a structural
      // edit made during it). It is a pending write, not device truth, so it is left out
      // of the snapshot entirely and the next diff sends it.
      const known = !covered(c.node) ? prior?.get(k) : device ? device.get(k) : c.vdValue;
      if (known !== undefined) this.snapshot.set(k, known);
    }
    for (const [k, v] of kept) if (v !== undefined && this.snapshot.has(k)) this.snapshot.set(k, v);
    this.rebuildFollowSet(model, plan, scope, commands);
    for (const w of planToNameWrites(model, deviceView ?? plan)) {
      const key = nameKey(w);
      const value = covered(w.node) && (readNames || w.name !== undefined) ? w.value : priorNames?.get(key);
      if (value !== undefined) this.nameSnapshot.set(key, value);
    }
    if (since !== undefined) {
      // Restore what the view could not know: a notify the device sent after the read was
      // issued. Confined to the addresses this capture registered, so the shape still comes
      // from the live plan alone.
      for (const [k, entry] of this.directJournal) {
        if (entry.at > since && this.index.has(k)) this.snapshot.set(k, entry.value);
      }
    }
    // An announcement the read behind this capture was issued after, on a node that read
    // covered (`nodes`; absent = the whole device), is carried by the rebuilt snapshot. One
    // that arrived while the read was in flight, or on a node it did not read, is still the
    // unit's last word.
    for (const [k, a] of this.announced) {
      if (since !== undefined && a.at > since) continue;
      if (unread?.has(k)) continue;
      const node = this.index.get(k)?.node;
      if (nodes && (node === undefined || !nodes.has(node))) continue;
      this.announced.delete(k);
    }
    if (this.onHeld) this.schedule();
  }

  /**
   * Rebuild the follow address set and its index from `commands`, and mark it for
   * re-registration. The SNAPSHOT is not touched: this runs mid-flush for an edit that
   * reshapes the set without a device read behind it, where the snapshot must go on
   * holding what the device was last known to have.
   *
   * Called from `capture()` with its own command list, and from `flush()` when the
   * emitted set moved under an edit that reaches no capture at all — a send wire is the
   * one that does, since no SEND_* param carries a `sideEffect`.
   */
  private rebuildFollowSet(model: DeviceModel, plan: Plan, scope: WriteScope, commands: VdCommand[]): void {
    this.index.clear();
    const addrs: Array<[number, number, number]> = [];
    for (const c of commands) {
      addrs.push([c.paramId, c.x, c.y]);
      const direct = (PARAMS as Record<string, ParamSpec>)[c.name].follow === "direct";
      this.index.set(cmdAddr(c), { name: c.name, node: c.node, direct });
    }
    // Name addresses join the registration set. They are not in `planToCommands`
    // (the app writes names through a separate string path), so for the life of the
    // follow layer they were in no registration and `Subs::absorb` dropped every
    // rename notify — the unit announces one, measured on a URX44V, and nothing was
    // listening. ~17 addresses against ~800, and a name notify only fires when
    // someone renames, so the steady-state cost is nil.
    this.nameIndex.clear();
    for (const node of model.nodes) {
      const nc = nameControl(model, node.id);
      if (!nc) continue;
      for (const y of nc.instances) {
        addrs.push([nc.param, 0, y]);
        this.nameIndex.set(addrKey(nc.param, 0, y), node.id);
      }
    }
    // The string writes that ARE a catalog row — the SSMCS preset — join it too. Without
    // this they were in no list at all rather than merely unsubscribed: this method is what
    // builds the candidate set, and it read `planToNameWrites` only for the name snapshot,
    // so a catalog string address could never become a candidate however many times the
    // follow layer re-subscribed. Indexed to the owner rather than into `nameIndex`: it is
    // not a name, so a notify for it takes that node's scoped read instead of being placed
    // directly. That is what makes a preset changed ON the unit followable, and what lets
    // the flush's own settle end on the unit's announcement rather than at its bound.
    //
    // From the LIVE plan, not the view — the same split the numeric loop above takes, where
    // the SHAPE is the plan's and only the VALUES come from the view. A view predates the
    // read it was taken for, so an edit made during that read is in the plan and not in it;
    // building the candidate set from the view would drop the address until some later
    // reconcile, which for a string address means the preset a person just chose is unheard.
    for (const w of planToNameWrites(model, plan)) {
      if (w.name === undefined || w.node === undefined) continue;
      addrs.push([w.param, 0, w.y]);
      const direct = (PARAMS as Record<string, ParamSpec>)[w.name].follow === "direct";
      this.index.set(addrKey(w.param, 0, w.y), { name: w.name, node: w.node, direct });
    }
    // Addresses the app READS but never writes, enumerated by translate beside the emit
    // decision they mirror. Same consumer shape as the loop above: register, and index to
    // the node whose scoped read repairs the value. The registration is therefore no
    // longer "the addresses the app writes" — see `followAddrs`.
    //
    // Scoped like the emitted set, and by the same `sceneExternal` predicate: under
    // *Scene only* a whole-device read puts the plan's scene-external values back
    // afterwards, so following one here would make the notify path and the full-read path
    // disagree about the same value under the same preference.
    for (const f of planToFollowOnlyAddrs(model, plan, scope)) {
      addrs.push([f.param, f.x, f.y]);
      const direct = !f.scoped && (PARAMS as Record<string, ParamSpec>)[f.name].follow === "direct";
      this.index.set(addrKey(f.param, f.x, f.y), { name: f.name, node: f.node, direct });
    }
    this.followAddrList = addrs;
    this.followSetStale = true;
  }

  /**
   * Whether `commands` is the emitted set the follow list was last built from. The
   * numeric addresses are its prefix, in command order, so this is an integer compare per
   * address and no string building — cheaper than the identity join the follow layer does
   * when it is asked to re-register.
   */
  private followSetMatches(commands: VdCommand[]): boolean {
    if (this.followAddrList.length < commands.length) return false;
    for (let i = 0; i < commands.length; i++) {
      const a = this.followAddrList[i];
      const c = commands[i];
      if (a[0] !== c.paramId || a[1] !== c.x || a[2] !== c.y) return false;
    }
    return true;
  }

  /** Resolve an incoming device notify address to its catalog name, owner node,
   *  and follow strategy — or undefined when the address is not in the writable
   *  set (the caller treats that as an unknown change worth a full reconcile). */
  lookup(paramId: number, x: number, y: number): FollowAddr | undefined {
    return this.index.get(addrKey(paramId, x, y));
  }

  /** The node a name address belongs to, or undefined when it is not one. */
  lookupName(paramId: number, x: number, y: number): string | undefined {
    return this.nameIndex.get(addrKey(paramId, x, y));
  }

  /** The string-path echo test. Separate from `isEcho` because names live in their
   *  own snapshot: the numeric one has no entry for them, so asking it would call
   *  the app's own rename a device-side change and bounce it back. */
  isEchoName(paramId: number, y: number, value: string): boolean {
    const k = `${paramId}:${y}`;
    return this.takePending(this.pendingNames, k, value) || this.nameSnapshot.get(k) === value;
  }

  /** Record a name the device reported, so the next flush does not re-send it and
   *  the next notify for it reads as an echo. The string half of `noteDirect`. */
  noteDirectName(paramId: number, y: number, value: string): void {
    this.nameSnapshot.set(`${paramId}:${y}`, value);
  }

  private async flush(): Promise<void> {
    if (!this.active) return;
    if (this.flushing) {
      this.pending = true;
      return;
    }
    this.flushing = true;
    // Asked again by this flush: a held ON it still cannot send sets it back.
    this.onHeld = false;
    // The session this flush is for. `model` and `plan` below are captured once and the
    // re-take at the head of the loop reads those captures, so once the generation moves
    // both describe a document that is no longer open — which is the same instant at
    // which nothing may be sent any more. One check answers both, and the catch asks it
    // too.
    const gen = this.sessionGen;
    const signal = this.sessionAbort.signal;
    let finished = (): void => {};
    this.flushDone = new Promise<void>((resolve) => (finished = resolve));
    let edits: PlanWriteWatch | undefined;
    try {
      const model = this.hooks.getModel();
      const plan = this.hooks.getPlan();
      let sent = 0;
      let sideEffect = false;
      const refetch = new Set<string>();
      // Per node, the command names a refetch head written in THIS flush handed to the
      // device (ParamSpec.drives). Only a converge in the same flush reads it: it would
      // otherwise send the plan's pre-write copies of exactly these, undoing what the head
      // just computed. Scoped to the node, because the same names on another channel are
      // that channel's own and the converge is right about them.
      const driven = new Map<string, Set<string>>();
      // What the sideEffect heads this flush wrote have reset on the unit, per family and
      // node — what the converge below is for, and so what the park in front of it leaves
      // alone. A head that resets nothing the park reads adds nothing here, so its node's
      // other families are still parked.
      const convergeResets = new Set<string>();
      // Addresses the NAME loop wrote that the refetch may not start before hearing about,
      // each at the mark taken before its own write. Separate from `writes` because that map
      // is numeric and the read overlays answers from it; see the name loop.
      const nameSettle = new Map<number, number>();
      // Every address the NAME loop wrote, at its own mark — the announcement obligation,
      // which is a different question from nameSettle's (that one is only about the
      // addresses a refetch is about to read). A name write reaches the unit only after the
      // loop has found the value differs from the snapshot, so each one here is a write the
      // unit must announce, the same test `changed` makes on the numeric side. Kept out of
      // `writes`: that map answers a numeric read overlay and a string notify carries its
      // text in a different field, so the value has no place there — but the ADDRESS and the
      // mark are all an announcement watch needs.
      const nameWrites = new Map<number, number>();
      // The text each rename sent, so a run of renames on one address is judged the way a
      // run of fader moves is: the unit answers the run with one notify carrying the last
      // name, and a notify carrying an EARLIER one says that rename was announced on its
      // own and says nothing about the one that replaced it.
      const nameExpected = new Map<number, string>();
      // What this flush wrote and the device acked, keyed the way the snapshot is
      // (translate.addrKey). Handed to the refetch, whose read would otherwise be issued
      // inside the window in which the unit still answers these addresses with the
      // values these writes replaced. Bounded by the flush's own diff.
      //
      // `mark` is taken immediately before that address's own vdSet, because only a
      // notify after it can be the announcement of that write; one mark for the whole
      // flush would let a device-side notify for the fader confirm a write the loop had
      // not reached yet. `changed` says the snapshot held a DIFFERENT value, so the unit
      // must change and must announce it — as against an address the snapshot held NO
      // entry for, whose write may be a value the unit already holds, and a no-op write
      // emits no notify at all (measured). `node` decides which of the two subsets below
      // the address lands in, and cannot be resolved until the loop has finished: the
      // command that puts a node into `refetch` may come after the ones that wrote it.
      const writes = new Map<number, { mark: number; node?: string; changed: boolean; value: number }>();
      const scope = this.scope();
      let commands = planToCommands(model, plan, scope);
      // The set can only be rebuilt by a capture, and a flush reaches one only through a
      // `sideEffect` param's converge or refetch epilogue — so an edit that moves the set
      // with no such head in it would leave the registration behind until something
      // reconciled. The comparison is an integer per address and holds that by
      // construction, whichever edit moved the set.
      // Taken before the first await so the rebuild cannot interleave with a capture, and
      // the snapshot is left alone — nothing has been read.
      if (!this.followSetMatches(commands)) this.rebuildFollowSet(model, plan, scope, commands);
      // What the heads in THIS flush are about to reset on the unit, read BEFORE the first
      // write goes out. A head write refills those families with the incoming type's factory
      // values, and the plan is already holding the incoming selection — so the outgoing
      // effect exists on the unit alone, and nothing announces it (params.ts). Taken here
      // rather than at the control that moved the head, every writer is covered by one
      // boundary: both EFFECT TYPE selectors, an undo of one, the insert-FX selector, a MIDI
      // mapping, a plan load.
      //
      // `keepHeads`: the values are filed under the keys the head the UNIT is on owns, and
      // the selection this flush is about to carry stays the operator's. What the park
      // leaves in the plan is a DIFF against the snapshot, which is what puts those values
      // back on the unit after the head write resets them — the converge's own job for
      // everything else.
      const outgoing = new Set<string>();
      for (const c of commands) {
        const family = RESETS.get(c.name);
        if (family === undefined || c.node === undefined) continue;
        if (this.snapshot.get(cmdAddr(c)) === c.vdValue) continue;
        outgoing.add(silentKey(family, c.node));
      }
      if (outgoing.size) {
        await this.hooks.parkSilent?.({ only: outgoing, keepHeads: true });
        if (this.sessionGen !== gen) return;
        // Derived again: the park merged the unit's own values into the plan.
        commands = planToCommands(model, plan, scope);
      }
      // A source's D.Gain the snapshot holds no entry for is one this session has not sent and
      // no capture holds: the write carries it only while some channel reads the source, so a
      // channel routed back to one takes it out of the plan's copy, which a panel move there
      // never updates. Read before it goes out, as the head writes are.
      const reentering = new Set<string>();
      for (const c of commands) {
        if (c.name !== "D_GAIN" || c.node === undefined) continue;
        if (this.snapshot.get(cmdAddr(c)) !== undefined) continue;
        reentering.add(silentKey("dGain", c.node));
      }
      if (reentering.size) {
        await this.hooks.parkSilent?.({ only: reentering });
        if (this.sessionGen !== gen) return;
        commands = planToCommands(model, plan, scope);
      }
      // A write that provokes a refetch has that read issued only once the flush's writes have
      // returned, and the read covers the whole node — so an edit made after this flush took its
      // values, to the head or to anything else that node holds, is carried by none of the
      // writes. When the values taken here hold such a write, among the numbers or the names, the
      // watch opens here, before anything is sent, and goes to the read, which keeps that edit as
      // it keeps one made while it runs. A head that turns up only later — in a re-take, or in the
      // list the name loop takes for itself — while no watch is open is left for the next flush,
      // which opens one from its own start: no read follows a send nothing watched.
      const numericHead = (c: VdCommand): boolean => c.node !== undefined && REFETCH.has(c.name);
      const nameHead = (w: NameWrite): boolean => w.name !== undefined && REFETCH.has(w.name);
      if (
        commands.some((c) => numericHead(c) && this.snapshot.get(cmdAddr(c)) !== c.vdValue) ||
        planToNameWrites(model, plan).some((w) => nameHead(w) && this.nameSnapshot.get(nameKey(w)) !== w.value)
      )
        edits = this.hooks.watchEdits?.();
      // A head nothing is watching for goes out with the next flush instead of this one.
      const unwatched = (): boolean => this.hooks.watchEdits !== undefined && edits === undefined;
      // Both lists below are frozen at flush start; the snapshots they are diffed against
      // are not. Any await can let a device-side change land (noteDirect's one entry, or a
      // reconcile's whole capture), and what a frozen list carries is then older than what
      // the plan holds. `fresher` re-takes the values on the first command after that
      // happened, so what goes out is a value the plan holds NOW and a device-authored one
      // can never be sent back at the device as an app write.
      //
      // Only the VALUES are re-taken. The ORDER stays this flush's own, because order binds
      // meaning (a type selector types the array after it), and an address the plan only
      // just grew stays out — that is a pending app edit, and its own markChanged has
      // already scheduled the trailing flush that carries it.
      //
      // A re-take is the same ~0.13 ms translate the flush opened with, and it is paid only
      // in the windows where a notify or a reconcile actually landed inside the loop.
      let valuesAt = this.snapshotEpoch;
      let fresh: Map<number, number> | null = null;
      // The frozen list's values by address, built the first time a command asks for another's.
      let frozen: Map<number, number> | null = null;
      for (const c of commands) {
        const k = cmdAddr(c);
        if (this.snapshotEpoch !== valuesAt) {
          valuesAt = this.snapshotEpoch;
          fresh = this.commandValues(model, plan, scope);
        }
        // Absent from the re-take = the address left the plan while this flush ran, so
        // there is nothing left to send to it.
        const value = fresh ? fresh.get(k) : c.vdValue;
        if (value === undefined) continue;
        const had = this.snapshot.get(k);
        if (had === value) continue;
        if (numericHead(c) && unwatched()) {
          this.pending = true;
          continue;
        }
        // +48V or HI-Z ON while the unit holds the other one on: not sent, and left in the
        // plan rather than dropped (see `onHeld`). The command order sends a switch the plan
        // turns off ahead of the other one's ON, so a flush moving from one to the other
        // has already written the OFF into the snapshot by the time it asks.
        const excludedBy = onExcludedBy(c, value);
        if (excludedBy !== null && this.unitHolds(excludedBy)) {
          this.onHeld = true;
          continue;
        }
        // The unit is asked for the other switch just before the ON goes: the broker answers
        // with a change the unit has made from the moment it announces it, which is ahead of
        // that announcement reaching this session. An ON it answers is kept as the unit's last
        // word (`announced`), so the A.Gain the same press lowered waits with the switch.
        if (excludedBy !== null) {
          const other = await vdGet(otherSwitchId(c)!, c.x, c.y);
          if (this.sessionGen !== gen) return;
          if (other) {
            this.announced.set(excludedBy, { value: other, at: ++this.directSeq });
            this.onHeld = true;
            continue;
          }
        }
        // A.Gain waits with a HI-Z ON held on its channel (`carrierOf`), in this flush or the
        // one before it: the press that lowered it has not reached the unit, and the read that
        // lands may refuse it.
        const carrier = carrierOf(c, value);
        if (
          carrier !== null &&
          this.onWaits(carrier, fresh ?? (frozen ??= new Map(commands.map((x) => [cmdAddr(x), x.vdValue] as const))))
        ) {
          this.onHeld = true;
          continue;
        }
        // A Clip Safe OFF behind an A.Gain write this session sent to the same jack waits for
        // the unit to announce that A.Gain.
        await this.clipSafeHold.before(c, value, signal);
        if (this.sessionGen !== gen) return;
        // Taken before the send, not after it: a notify that lands while this very
        // vdSet is in flight cannot be placed on either side of it, and the safe
        // reading is that it is the answer to this write. Such a notify is also one
        // `hasUnannouncedWrite` answers for, so the follow layer re-reads its node
        // rather than putting its value into the plan (settle.ts).
        const mark = writeSettle.mark();
        this.clipSafeHold.sending(c, value, mark);
        this.inFlight.set(k, value);
        try {
          await vdSet(c.paramId, c.x, c.y, value);
        } catch (e) {
          throw withGuess(e, unverifiedGuessAt(model, c.paramId, c.y));
        } finally {
          this.inFlight.delete(k);
        }
        // Nothing below this line belongs to a session that has gone: the remaining
        // commands would go out over a disconnected link, and the snapshot they would be
        // recorded in has already been rebuilt by whatever ended (or replaced) it.
        if (this.sessionGen !== gen) return;
        this.snapshot.set(k, value);
        this.announced.delete(k);
        if (!this.echoedInFlight.delete(k)) this.notePending(this.pendingValues, k, value);
        writes.set(k, { mark, node: c.node, changed: had !== undefined, value });
        this.recentWrites.set(k, { mark, node: c.node, at: Date.now() });
        sent++;
        if (CONVERGE.has(c.name)) {
          sideEffect = true;
          // Whose values the unit is resetting. The park below leaves exactly these to the
          // converge, which is what puts them back.
          const family = RESETS.get(c.name);
          if (family !== undefined && c.node !== undefined) convergeResets.add(silentKey(family, c.node));
        } else if (REFETCH.has(c.name) && c.node) {
          refetch.add(c.node);
          const drives = DRIVES.get(c.name);
          if (drives) {
            let names = driven.get(c.node);
            if (!names) driven.set(c.node, (names = new Set()));
            for (const n of drives) names.add(n);
          }
        }
      }
      // The name loop needs the same guard for the same reason: capture() rebuilds the NAME
      // snapshot too, so a reconcile resolving inside one of these awaits leaves the frozen
      // list carrying a string the plan has stopped holding. Rarer than a value — only a
      // readback can bring a device-side name in — and never yet caught in the harness, but
      // it is one structure, so it gets one rule.
      let namesAt = this.snapshotEpoch;
      let freshNames: Map<string, string> | null = null;
      for (const w of planToNameWrites(model, plan)) {
        const k = nameKey(w);
        if (this.snapshotEpoch !== namesAt) {
          namesAt = this.snapshotEpoch;
          freshNames = new Map(planToNameWrites(model, plan).map((n) => [nameKey(n), n.value]));
        }
        const value = freshNames ? freshNames.get(k) : w.value;
        if (value === undefined) continue;
        if (this.nameSnapshot.get(k) === value) continue;
        if (nameHead(w) && unwatched()) {
          this.pending = true;
          continue;
        }
        // Before the write, for the reason the numeric loop takes one: only a notify after
        // it can be this write's announcement.
        const nameMark = writeSettle.mark();
        // A node rename carries no `name` (translate.NameWrite): it has no catalog row, so
        // it can never be a sideEffect head and nothing reads it back. The
        // catalogued string writes — the SSMCS preset — are read by their own refetch, and
        // watching them here as well would report one silence twice.
        const nameAddr = addrKey(w.param, 0, w.y);
        this.inFlightNames.add(k);
        try {
          await vdSetStr(w.param, 0, w.y, value);
        } finally {
          this.inFlightNames.delete(k);
        }
        if (this.sessionGen !== gen) return;
        this.nameSnapshot.set(k, value);
        this.notePending(this.pendingNames, k, value);
        if (w.name === undefined) {
          nameWrites.set(nameAddr, nameMark);
          nameExpected.set(nameAddr, value);
        }
        sent++;
        // A string write can be a sideEffect head too: the SSMCS preset recomputes the strip
        // exactly as the morphing knob does. Only REFETCH is consulted — no string param is a
        // converge head, and a branch nothing reaches is a claim no test can hold.
        if (w.name !== undefined && w.node !== undefined && REFETCH.has(w.name)) {
          refetch.add(w.node);
          const drives = DRIVES.get(w.name);
          if (drives) {
            let names = driven.get(w.node);
            if (!names) driven.set(w.node, (names = new Set()));
            for (const n of drives) names.add(n);
          }
          // The read may not start before the unit has spoken for this address — the rule the
          // numeric writes take. The unit does announce the preset write on its own address,
          // first (params.ts), and capture() now puts that address in the candidate set so
          // the app can hear it. What it does NOT decide is WHEN the follow layer subscribes
          // to a candidate: that happens after a reconcile, so an address the plan grew from
          // an app-side edit — this one and the numeric SSMCS block alike (measured, t2b) —
          // is unheard until one runs, and this wait resolves at its bound instead. Either
          // way the read does not start inside the write's staleness window, which is what
          // this line is for. It goes into
          // `mustSettle`, with its mark travelling as `boundaryMarks` rather than in the
          // `written` map: that map is what the read overlays its answers from, it is numeric,
          // and a string notify carries its text in a different field — so an entry there
          // would answer a numeric read with a number the unit never sent. The mark is
          // separate but not optional: without one the wait is held unconditionally
          // (settle.ts), so an announcement that arrived while the rest of this flush ran —
          // which is the ordinary case, the unit answers in about 15 ms — would be missed and
          // the read would spend the whole bound anyway.
          nameSettle.set(addrKey(w.param, 0, w.y), nameMark);
        }
      }
      this.lastFlushConverged = sideEffect;
      if (sideEffect) {
        // The device reset dependents; converge against its post-reset state and
        // rebuild the snapshot so the next diff measures from the device truth.
        // Converge against a frozen copy, not the live plan: an edit that arrives
        // during the (awaited) converge must stay a diff for the trailing flush,
        // not get baked into the snapshot here as if already on the device (which
        // would silently drop it). The mark is taken beside the freeze, for the
        // other half of the same window: a direct notify arriving during the
        // converge is device truth this copy is too old to carry.
        // Ahead of the freeze, so what it reads is in the copy the converge sends from: a
        // converge re-sends whatever differs across the whole scope, and for the three
        // silent families the plan's copy can be arbitrarily old. Its own node is left out
        // — that is the one the head just reset, and the converge is what restores it.
        await this.hooks.parkSilent?.({ exclude: convergeResets });
        if (this.sessionGen !== gen) return;
        const since = this.directSeq;
        const converged = structuredClone(plan);
        // The loop seeds its own diff by reading the device, and this flush wrote
        // that device a millisecond ago — so the same handle the refetch gets goes
        // here too, and for the same reason. `mustSettle` is left to the loop:
        // its read covers the whole write scope, not one node's worth (see
        // client.ts). Only the marks and the addresses matter here; the loop
        // reads the unit rather than answering from an announcement, because
        // what it is after is the values the unit RESET, which are not these.
        const seedPending: PendingWrites = {
          written: new Map([...writes].map(([k, w]) => [k, w.mark])),
          mustSettle: new Set(writes.keys()),
          mustAnnounce: new Set(),
        };
        // The addresses a refetch head in this same flush has just handed to the device.
        // Resolved here rather than in the loop because a command that names one of them
        // can come BEFORE the head that drives it, and `driven` is only complete once the
        // loop has finished. `converged` is a clone of the same plan, so its command list
        // carries the same addresses.
        const exclude = new Set<number>();
        if (driven.size)
          for (const c of commands) {
            if (c.node !== undefined && driven.get(c.node)?.has(c.name)) exclude.add(cmdAddr(c));
          }
        // A switch the unit turned on at its own panel, which no read has brought into the plan
        // yet, is the unit's: the converge would write the plan's older OFF over it. It leaves
        // that switch and the other one on the same channel to the follow read the announcement
        // scheduled, and the capture below learns nothing about either.
        const ownOns = new Set<number>();
        for (const c of commands) for (const k of this.ownOnPair(c)) ownOns.add(k);
        for (const k of ownOns) exclude.add(k);
        // A refetch head the operator moved that this flush did not send — left for the next
        // flush, passed at a value the plan no longer holds, or at an address that first appeared
        // while the flush ran — is not the converge's to send: the unit would recompute what it
        // drives with no read behind it, and the converge would write the plan's older copies
        // back over the recomputation. It stays out of the converge's reads and sends, and the
        // capture below keeps what the snapshot says the unit holds there — nothing, for a new
        // address — names included, so the next flush sends it and reads its node back.
        const unsentHeads = new Map<number, number | undefined>();
        for (const c of planToCommands(model, converged, scope)) {
          if (!numericHead(c)) continue;
          const k = cmdAddr(c);
          const had = this.snapshot.get(k);
          if (c.vdValue !== had) unsentHeads.set(k, had);
        }
        for (const k of unsentHeads.keys()) exclude.add(k);
        const unsentNameHeads = planToNameWrites(model, converged)
          .filter((w) => nameHead(w) && this.nameSnapshot.get(nameKey(w)) !== w.value)
          .map((w) => [nameKey(w), this.nameSnapshot.get(nameKey(w))] as const);
        this.converge = { ownOns, exclude };
        // The converge's writes are this session's as much as the send loop's, and the unit
        // announces them the same way: each is registered as the loop registers its own, so
        // its announcement is taken as its echo rather than as a change on the unit's panel.
        // The copy it sends from can hold a value the operator has moved past since.
        const marks = new Map<number, number>();
        const r = await sendConverging(model, converged, {
          scope: this.scope(),
          pending: seedPending,
          exclude,
          signal,
          onSending: (c) => {
            const k = cmdAddr(c);
            marks.set(k, writeSettle.mark());
            this.inFlight.set(k, c.vdValue);
          },
          onSent: (o) => {
            if (this.sessionGen !== gen) return;
            const k = cmdAddr(o.command);
            this.inFlight.delete(k);
            const echoed = this.echoedInFlight.delete(k);
            if (o.result !== "accepted") return;
            if (!echoed) this.notePending(this.pendingValues, k, o.command.vdValue);
            // A move on the unit's panel the journal holds for this address is one this write
            // has replaced, so the capture behind the converge does not restore it. One that
            // arrives later is journaled again then.
            this.directJournal.delete(k);
            this.recentWrites.set(k, { mark: marks.get(k)!, node: o.command.node, at: Date.now() });
          },
        }).finally(() => {
          this.converge = null;
        });
        // Same rule as the send loops, and before the failure check on purpose: once the
        // session is gone there is nobody left to report a failed converge to, and the
        // capture below would write a dead plan's values into a rebuilt snapshot.
        if (this.sessionGen !== gen) return;
        // `converged` is what the loop sent and read back, so it is what the addresses mean.
        // Ahead of the failure check, because a round that failed leaves every earlier round's
        // confirmations standing and this is the last chance the plan has to take them: the
        // address stops differing the moment the write lands, so no later flush produces a
        // diff that would offer it again.
        this.hooks.onConfirmed?.(confirmedAddrs(r.ledger), converged);
        // sendConverging reports per-command failures instead of rejecting, so a
        // failed write here would otherwise be swallowed — and captureSnapshot
        // would then record the plan as device truth, leaving those parameters
        // diverged for the rest of the session with no diff left to retry them.
        // Route it into the same teardown a direct write failure takes.
        const failed = r.outcomes.find(reachedAndFailed);
        if (failed || r.readErrors.length) {
          // `||` throughout: an empty message is what a rejection with no reason
          // leaves behind, and `new Error("")` here would end the session with a
          // teardown that names nothing. The read's own message rather than the report
          // entry, which carries the parameter's name in front of the shell's code.
          const at = failed?.command ?? r.unread[0];
          throw withGuess(
            new Error(failed?.error || r.readCauses[0] || "converge-failed"),
            at && unverifiedGuessAt(model, at.paramId, at.y),
          );
        }
        this.capture(converged, since, undefined, new Set([...ownOns, ...unsentHeads.keys()]));
        for (const [k, v] of unsentHeads) if (v === undefined) this.snapshot.delete(k);
        for (const [k, v] of unsentNameHeads) {
          if (v === undefined) this.nameSnapshot.delete(k);
          else this.nameSnapshot.set(k, v);
        }
      }
      // A refetch after the converge, if both happened: converge rebuilds the snapshot
      // from the plan, and the read that follows is what makes the plan right.
      if (refetch.size && this.hooks.refetchNodes) {
        // Sampled before the call, which takes its private copy synchronously — so the
        // mark and the copy describe the same instant. The read's own settle happens
        // after that copy, so a direct notify landing in it is device truth the copy
        // predates, which is exactly what this mark restores.
        const since = this.directSeq;
        // Split by whether this read is going to ASK the unit about the address.
        //
        //   - inside its scope: the read re-reads that whole node, so this is an address
        //     it may ask the unit about and it may not start before the unit has spoken
        //     for it. By node rather than by which addresses the pass actually reads,
        //     because the two lists would then have to agree for ever and the cost of
        //     the conservative reading is one wait the pass shares with its neighbours
        //     anyway. A changed write ends that wait at its own notify
        //     (9-204 ms measured, and the collateral the read is FOR went fresh 1-2 ms
        //     behind it); one the snapshot held no entry for can only end at the bound,
        //     since it may be a no-op the unit never announces.
        //   - outside it: not read, so nothing here confirms or repairs it. A CHANGED
        //     one the unit never announces is a write that went nowhere, and the settle
        //     reports it to the follow side, which arms its idle full reconcile — the
        //     only repair available without widening this read, which is what the
        //     scoping is for. A no-op write legitimately says nothing and is left alone.
        //
        // Nothing is withdrawn from this handle, and nothing needs to be. The read is
        // answered from what the unit ANNOUNCED, and the last announcement wins — so an
        // address the operator moved on the board after our write comes back carrying
        // THEIR value, which is the answer the withdrawal used to reach by falling back
        // to a read. A capture() landing inside the flush needs no answer either: it
        // re-authors the snapshot from a device read, and a device read cannot contradict
        // a later word from the same device.
        const written = new Map<number, number>();
        const mustSettle = new Set<number>();
        const mustAnnounce = new Set<number>();
        // What each write SENT, so the settle can tell a superseded write's silence from a
        // discarded one's — the two produce the same single notify and differ only in the
        // value it carries (settle.ts, SettleOptions.expected).
        const expected = new Map<number, number>();
        for (const [k, w] of writes) {
          written.set(k, w.mark);
          expected.set(k, w.value);
          if (w.node !== undefined && refetch.has(w.node)) mustSettle.add(k);
          else if (w.changed) mustAnnounce.add(k);
        }
        for (const k of nameSettle.keys()) mustSettle.add(k);
        const deviceView = await this.hooks.refetchNodes(
          refetch,
          {
            written,
            mustSettle,
            mustAnnounce,
            expected,
            ...(nameSettle.size ? { boundaryMarks: nameSettle } : {}),
          },
          edits,
        );
        if (this.sessionGen !== gen) return;
        // The read ran against its own copy of the plan (readback.readIntoPlan), so the
        // copy is what the device holds: re-base from it and an edit made during the
        // await — on the read node or any other — stays a diff. Null = the plan it read
        // into is gone, and there is nothing a snapshot could describe.
        if (deviceView) this.capture(deviceView, since, refetch, undefined, false);
      }
      // A flush with NEITHER epilogue — the ordinary edit: a fader, a mute, a pan, a rename
      // — issues no read at all, so nothing here would ever notice the unit silently
      // dropping one of its writes. The rename belongs in that list and is in the watch
      // below because it is put there explicitly; it does not ride in `writes`, which the
      // numeric loop alone fills. The two epilogues each cover their own: a
      // converge re-reads the whole write scope and re-sends the residual, and the
      // refetch hands its own `mustAnnounce` to the settle above. This is the third case,
      // and it gets the watch without the wait: there is no read to hold open, and the
      // report is what arms follow's idle full sweep.
      //
      // `changed` is the obligation test the settle states: the write went out only
      // because the snapshot held a DIFFERENT value, so the unit must move and must say
      // so. A write the snapshot had no entry for may be a no-op, announces nothing, and
      // must stay out or every flush would order a sweep.
      if (!sideEffect && !(refetch.size && this.hooks.refetchNodes)) {
        const announce = new Set<number>();
        const marks = new Map<number, number>();
        const expected = new Map<number, number>();
        for (const [k, w] of writes) {
          marks.set(k, w.mark);
          expected.set(k, w.value);
          if (w.changed) announce.add(k);
        }
        if (announce.size) writeSettle.watch(marks, announce, { expected });
      }
      // Renames are watched WHATEVER the epilogue, because neither epilogue covers them:
      // the converge re-reads numeric commands, and the refetch is the one caller that
      // asks applyNodeState to skip the names (main.ts). So a rename sharing a flush with
      // a COMP 1-knob — which an external MIDI control can send while a name is being
      // typed — fell outside the guard above and was watched by nothing. The unit
      // announces a name change on the name address and stays silent for a write of the
      // value it already holds, the same pair of rules the numeric side has, and the loop
      // skips the second case before writing, so every entry here is owed an announcement.
      //
      // Left unwatched a dropped rename is invisible twice: the plan and the name snapshot
      // both move to the new name, so no later flush finds a diff to re-send.
      if (nameWrites.size) writeSettle.watch(nameWrites, new Set(nameWrites.keys()), { expected: nameExpected });
      // Before onSent, and unconditional on `sent`. A converge cannot be the reason — its
      // flag is set after `sent++`, so a flush that sent nothing never reaches one — but
      // `resync()` captures OUTSIDE any flush (the caller runs it after every device-side
      // reconcile), and the latch it leaves is consumed by whichever flush comes next,
      // whatever that one sends. The callee compares the set, so this costs nothing when
      // it has not moved.
      if (this.followSetStale) {
        this.followSetStale = false;
        this.hooks.reregister?.();
      }
      if (sent) this.hooks.onSent(sent);
      // After onSent because the status line is last-writer-wins, and never from
      // capture(), whose report the session's own "live sync on" would overwrite.
      const owners = collisionOwners(commands);
      const key = collisionKey(owners);
      if (key !== this.collapsedKey) {
        this.collapsedKey = key;
        if (owners.length) {
          console.warn("live: one device address, more than one plan owner", owners);
          this.hooks.onCollapsed(owners);
        }
      }
    } catch (e) {
      // A failure that arrives once its session has ended — a write the link answered late,
      // or a converge the end aborted — is that session's, and the one running now is not
      // stopped by it.
      if (this.sessionGen !== gen) return;
      this.active = false;
      this.hooks.onError(e instanceof Error ? e.message : String(e), (e as { guess?: string } | null)?.guess);
      return;
    } finally {
      edits?.close();
      this.flushing = false;
      finished();
      // A flush asked for while this one ran. end() clears the request, so one standing here
      // after the session moved on is the next session's, and it is sent whichever way this
      // flush left — a flush whose session is stopped returns at its own entry.
      if (this.pending) {
        this.pending = false;
        void this.flush();
      }
    }
  }
}

/** `e` carrying the unverified mapping its address rests on, its message unchanged. */
function withGuess(e: unknown, guess: string | undefined): unknown {
  if (guess === undefined) return e;
  const err = e instanceof Error ? e : new Error(String(e));
  return Object.assign(err, { guess });
}
